import { GridCellParams } from '@mui/x-data-grid-pro';
import Chip from '@mui/material/Chip';
import { v4 } from 'uuid';
import { Link } from 'react-router-dom';
import { GridCustomColDef } from '../../types';
import { DetailCTARenderer } from '../../Compounds/UpstackDataGrid/CellRenderers/DetailCTARenderer';
import { ComponentName, getMetaData } from '../../Compounds/DetailsDrawer/detailsDrawersHelpers';
import { chipStyles, formatPercent, formatPrice, getDate, getFormattedDate } from '../../../utils/helpers';
import { LegacyArchiveReason } from '../../../utils/constants';
import {
  advancedDatePickerOperator,
  advancedNumericOperators,
  advancedOperators,
  advancedEnumOperators,
  runPickerOperators
} from '../../Compounds/UpstackDataGrid/helpers';
import { getCommissionsInfoByType } from '../../../store/GeneralStore';
import { assetGridColumnDefs } from './AssetGridColumnDefs';

export const DefaultBncAssetGridColumnDefs: GridCustomColDef[] = [
  {
    type: 'actions',
    field: 'details',
    cellClassName: 'details-cell',
    headerName: 'Details',
    sortable: false,
    hideable: false,
    width: 65,
    filterable: false,
    renderCell: (params: GridCellParams) => (
      <DetailCTARenderer
        data={params}
        value={''}
        valueFormatted={''}
        state={{
          id: v4(),
          data: params.row,
          ...getMetaData({ name: ComponentName.ASSET, sfId: params.row.sf_id, id: params.row.id, data: params.row })
        }}
      />
    ),
    pinned: 'left',
    disableReorder: true
  },
  {
    field: 'expectedFirstCommissionRun',
    headerName: 'Exp. First Run',
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 200,
    pinned: 'left',
    description: 'Drag to move column',
    filterOperators: runPickerOperators()
  },
  {
    field: 'actualFirstCommissionRun',
    headerName: 'Act. First Run',
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 200,
    pinned: 'left',
    description: 'Drag to move column',
    filterOperators: runPickerOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'lastCommissionRun',
    headerName: 'Last Run',
    sortable: true,
    filterable: true,
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    width: 150,
    description: 'Drag to move column',
    filterOperators: runPickerOperators(),
    hideByDefault: true
  },
  {
    field: 'number',
    headerName: 'Number',
    renderCell: (params: GridCellParams) => (
      <Link
        className="text-indigo"
        title="Fullscreen"
        to={`/asset/${params.row.id}`}>
        <div className="flex items-center text-small">{params.value as string}</div>
      </Link>
    ),
    sortable: true,
    filterable: true,
    width: 170,
    description: 'Drag to move column',
    pinned: 'left',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: getCommissionsInfoByType('account', 'salesAgents'),
    field: 'salesAgent',
    headerName: 'Sales Agent',
    sortable: true,
    filterable: true,
    width: 170,
    description: 'Drag to move column',
    filterOperators: advancedEnumOperators()
  },
  {
    field: 'directSales',
    headerName: 'Direct Sales',
    sortable: true,
    filterable: true,
    width: 170,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    getOptionLabel: (value) => String(value),
    getOptionValue: (value) => value,
    field: 'directSalesPaidPercent',
    headerName: 'Direct Sales %',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    valueFormatter: (value) => value,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params: GridCellParams) => (
      <span className={params.value && (params.value as number) < 0 ? 'text-price-red' : ''}>
        {formatPercent(params.value as number)}
      </span>
    ),
    hideByDefault: true
  },
  {
    type: 'singleSelect',
    valueOptions: getCommissionsInfoByType('account', 'suppliers'),
    field: 'supplierName',
    headerName: 'Supplier',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedEnumOperators()
  },
  {
    field: 'customer',
    headerName: 'Customer',
    renderCell: (params: GridCellParams) => (
      <Link
        className="text-indigo"
        title="Fullscreen"
        to={`/customer/${params.row.accountGri}`}>
        <div className="flex items-center text-small">{params.value as string}</div>
      </Link>
    ),
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    field: 'commissionsStatus',
    renderCell: (params: GridCellParams) => {
      const status = params.value as string;
      if (!status) return '';
      return (
        <Chip
          label={status}
          size="small"
          sx={chipStyles(status)}
          title={status}
        />
      );
    },
    headerName: 'Commissions Status',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: getCommissionsInfoByType('asset', 'commissionsStatus'),
    field: 'parentCommissionsStatus',
    renderCell: (params: GridCellParams) => {
      const status = params.value as string;
      if (!status) return '';
      return (
        <Chip
          label={status}
          size="small"
          sx={chipStyles(status)}
          title={status}
        />
      );
    },
    headerName: 'Parent Commissions Status',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedEnumOperators(true)
  },
  {
    field: 'assetStatus',
    headerName: 'Service Status',
    sortable: true,
    filterable: true,
    width: 150,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    field: 'tsd',
    headerName: 'TSD',
    sortable: true,
    filterable: true,
    width: 100,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    field: 'accountNumber',
    headerName: 'Account Number',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'ultimateParent',
    headerName: 'Ultimate Parent',
    renderCell: (params: GridCellParams) => (
      <Link
        className="text-indigo"
        title="Fullscreen"
        to={`/customer/${params.row.ultimateParentId}`}>
        <div className="flex items-center text-small">{(params?.value as string) ?? ''}</div>
      </Link>
    ),
    sortable: true,
    filterable: true,
    width: 170,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    field: 'supplierOrderNumber',
    headerName: 'Supplier Order Number',
    sortable: true,
    filterable: false,
    width: 200,
    description: 'Drag to move column'
  },
  {
    field: 'tsdTrackingNumber',
    headerName: 'TSD Tracking Number',
    sortable: true,
    filterable: false,
    width: 200,
    description: 'Drag to move column'
  },
  {
    field: 'circuitId',
    headerName: 'Circuit ID',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    field: 'locationName',
    headerName: 'Location Name',
    sortable: true,
    filterable: true,
    width: 150,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    field: 'locationAddress',
    headerName: 'Location Address',
    sortable: true,
    filterable: true,
    width: 150,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    field: 'totalMRC',
    headerName: 'Total MRC',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    headerAlign: 'right',
    align: 'right',
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && Number(params.value) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value as number)}
      </span>
    )
  },
  {
    field: 'commissionableMRC',
    headerName: 'Comm. MRC',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    headerAlign: 'right',
    align: 'right',
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && Number(params.value) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value as number)}
      </span>
    )
  },
  {
    field: 'grossMRR',
    headerName: 'Gross MRR',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    headerAlign: 'right',
    align: 'right',
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && Number(params.value) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value as number)}
      </span>
    )
  },
  {
    field: 'recurringCommissionPercent',
    headerName: 'Rec. Comm. %',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    valueFormatter: (value) => value,
    headerAlign: 'right',
    align: 'right',
    renderCell: (params: GridCellParams) => (
      <span className={params.value && Number(params.value) < 0 ? 'text-price-red' : ''}>
        {formatPercent(params.value as number)}
      </span>
    )
  },
  {
    field: 'contractSignDate',
    headerName: 'Contract Sign Date',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 100,
    description: 'Drag to move column',
    filterOperators: advancedDatePickerOperator()
  },
  {
    type: 'singleSelect',
    valueOptions: [],
    field: 'latestSalesAssignmentDate',
    headerName: 'Latest Sales Assignment Date',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 100,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedDatePickerOperator()
  },
  {
    field: 'installDate',
    headerName: 'Install Date',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 100,
    description: 'Drag to move column',
    filterOperators: advancedDatePickerOperator()
  },
  {
    field: 'billingCommencementDate',
    headerName: 'Billing Commencement Date',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedDatePickerOperator()
  },
  {
    field: 'disconnectDate',
    headerName: 'Disconnect Date',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 100,
    description: 'Drag to move column',
    filterOperators: advancedDatePickerOperator()
  },
  {
    field: 'commissionsExperienceAnalyst',
    headerName: 'CEA',
    sortable: true,
    filterable: false,
    width: 200,
    description: 'Drag to move column'
  },
  {
    field: 'sf_id',
    headerName: 'SFID',
    sortable: true,
    filterable: true,
    width: 170,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    field: 'id',
    headerName: 'ID',
    sortable: true,
    filterable: false,
    width: 300,
    description: 'Drag to move column',
    hideByDefault: true
  },
  {
    field: 'accountId',
    headerName: 'Account Id',
    sortable: true,
    filterable: true,
    width: 170,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    field: 'name',
    headerName: 'Name',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    field: 'supplierId',
    headerName: 'Supplier ID',
    sortable: true,
    filterable: true,
    width: 170,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    field: 'commissionsExperienceAnalystEmail',
    headerName: 'CEA Email',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    field: 'customerSuccessManager',
    headerName: 'CSM',
    sortable: true,
    filterable: false,
    width: 200,
    hideByDefault: true,
    description: 'Drag to move column'
  },
  {
    field: 'customerSuccessManagerEmail',
    headerName: 'CSM Email',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    field: 'commissionableNRC',
    headerName: 'Commissionable NRC',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedNumericOperators(),
    headerAlign: 'right',
    align: 'right',
    hideByDefault: true,
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && Number(params.value) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value as number)}
      </span>
    )
  },
  {
    field: 'netNRR',
    headerName: 'Net NRR',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedNumericOperators(),
    headerAlign: 'right',
    align: 'right',
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && Number(params.value) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value as number)}
      </span>
    )
  },
  {
    field: 'netMRR',
    headerName: 'Net MRR',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedNumericOperators(),
    headerAlign: 'right',
    align: 'right',
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && Number(params.value) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value as number)}
      </span>
    )
  },
  {
    field: 'nonCommissionableNRC',
    headerName: 'Non Commissionable NRC',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedNumericOperators(),
    headerAlign: 'right',
    align: 'right',
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && Number(params.value) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value as number)}
      </span>
    )
  },
  {
    field: 'grossNRR',
    headerName: 'Gross NRR',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedNumericOperators(),
    headerAlign: 'right',
    align: 'right',
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && Number(params.value) < 0 ? 'text-price-red' : ''}>
        {formatPrice(params.value as number)}
      </span>
    )
  },
  {
    field: 'contractNumber',
    headerName: 'Contract Number',
    sortable: true,
    filterable: true,
    hideByDefault: true,
    width: 200,
    description: 'Drag to move column',
    filterOperators: advancedOperators()
  },
  {
    field: 'nonRecurringCommissionPercent',
    headerName: 'Non Recurring Commission %',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedNumericOperators(),
    headerAlign: 'right',
    align: 'right',
    valueFormatter: (value) => value,
    renderCell: (params: GridCellParams) => (
      <span className={params.value && Number(params.value) < 0 ? 'text-price-red' : ''}>
        {formatPercent(params.value as number)}
      </span>
    )
  },
  {
    field: 'actualDecommissionedDate',
    headerName: 'Actual Decommission Run',
    sortable: true,
    filterable: true,
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    width: 100,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: runPickerOperators()
  },
  {
    field: 'estimatedDeliveryDate',
    headerName: 'Estimated Delivery Date',
    sortable: true,
    filterable: true,
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    width: 100,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedDatePickerOperator()
  },
  {
    field: 'upcomingRenewalDate',
    headerName: 'Upcoming Renewal Date',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedDatePickerOperator()
  },
  {
    field: 'cancelledDate',
    headerName: 'Cancelled Date',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedDatePickerOperator()
  },
  {
    field: 'activationDate',
    headerName: 'Activation Date',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 100,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedDatePickerOperator()
  },
  {
    field: 'expectedDecommissionDate',
    headerName: 'Expected Decommission Run',
    valueFormatter: (value) => {
      return getFormattedDate(value, 'MMM YYYY');
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 100,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: runPickerOperators()
  },
  {
    field: 'commissionsValidatedDate',
    headerName: 'Commissions Validated Date',
    valueFormatter: (value) => {
      return getFormattedDate(value);
    },
    valueGetter: (value) => {
      return getDate(value).getTime();
    },
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedDatePickerOperator()
  },
  {
    field: 'commissionsValidationStatus',
    headerName: 'Commissions Validation Status',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    field: 'commissionsValidationSubStatus',
    headerName: 'Commissions Validation Sub Status',
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedOperators()
  },
  {
    field: 'legacyArchiveReason',
    headerName: 'Legacy Archive Reason',
    valueOptions: Object.values(LegacyArchiveReason ?? {}),
    sortable: true,
    filterable: true,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true,
    filterOperators: advancedEnumOperators(true)
  },
  {
    field: 'expectedRpmAccountNumber',
    headerName: 'Expected RPM Account Number',
    sortable: true,
    filterable: false,
    width: 200,
    description: 'Drag to move column',
    hideByDefault: true
  },
  ...assetGridColumnDefs
];
